import React, { useEffect, useRef, useState } from "react";
import ExamPlanCompletionBoard from "./ExamPlanCompletionBoard";
import { useHistory } from "react-router-dom";
import { NORMALIZE_GRAD_CD, toExamDvsnCd } from "components/mypage/myExamInfo/examConstants";
import usexAPI from "store/customHooks/usexAPI";
import { xAPI_ID_VALUE } from "utils/xAPI_PATH";
import { EXAM_PLAN_XAPI_CONTENT } from "./examPlanXapi";
import "./ExamPlanHeader.scss";

/** 전광판(미션 완료 학생 이름) 노출 시작 : 시험대비모드 종료까지 남은 일수 */
const COMPLETION_BOARD_START_D_DAY = 14;

/** 남은 시간 단위 (일 · 시 · 분 · 초 순서) */
const REMAINING_TIME_UNITS = ["일", "시", "분", "초"];

const getRemainingTimeParts = (remainingSeconds) => {
    const safeSeconds = Math.max(0, Math.floor(remainingSeconds));
    const days = Math.floor(safeSeconds / 86400);
    const hours = Math.floor((safeSeconds % 86400) / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const seconds = safeSeconds % 60;

    // 일수가 100일 이상이면 자릿수가 늘어난 그대로 노출한다.
    return [days, hours, minutes, seconds].map((value) => String(value).padStart(2, "0"));
};

function ExamPlanHeader({ exam, showCountdown, completionMembers, mmbrSeq, planInfo }) {
    const history = useHistory();
    const { ServiceUnit_B } = usexAPI();
    // 톱니바퀴는 시험대비모드의 학년/학기/고사에 맞는 시험일정 등록/수정 팝업을 바로 띄운다.
    const openExamSchedule = () => {
        ServiceUnit_B(xAPI_ID_VALUE.serviceUnit.btn_epm_0010).setContent(EXAM_PLAN_XAPI_CONTENT).selected();
        const query = new URLSearchParams({
            EXAM_DVSN_CD: toExamDvsnCd(planInfo),
            GRAD_CD: NORMALIZE_GRAD_CD(planInfo?.GRAD_CD) || "",
        });
        history.push(`/mypage/myExamInfo?${query.toString()}`);
    };

    /**
     * 시험대비모드 기간이 이미 끝났는지. (END_D_DAY 음수 = 종료일 경과)
     * 시험기간을 입력하지 않은 채 기간이 끝나면 입력을 유도할 시점이 지났으므로
     * "시험기간을 입력해주세요." 대신 "시험기간 미입력" 로만 표시한다.
     */
    const planEndDDay = Number(planInfo?.END_D_DAY);
    const isPlanEnded = Number.isFinite(planEndDDay) && planEndDDay < 0;

    const examStart = exam.examStart?.valueOf() || 0;
    const examEnd = exam.examEnd?.valueOf() || 0;
    const now = Date.now();
    const isBeforeExam = examStart > now;
    const isExamEnded = examEnd > 0 && now > examEnd;
    const resolvedCountdownTarget = isBeforeExam ? examStart : isExamEnded ? 0 : examEnd;
    // 시험기간 미입력(시험기간을 몰라요) 상태에서는 남은 시간 대신 입력 안내만 노출
    const isCountdownActive = showCountdown && Boolean(exam.hasPeriod);

    /**
     * 전광판(미션 완료 학생 이름)은 시험대비모드 종료 2주 전부터 노출한다.
     * END_D_DAY 는 종료일까지 남은 일수(0 = 종료 당일, 음수 = 종료 경과)라 14 이하면 노출 구간이다.
     * 기존에는 시험 시작 2주 전 기준이었고, 시험기간을 입력하지 않으면 기간 내내 노출돼 요건과 달랐다.
     */
    const showCompletionBoard =
        showCountdown && Number.isFinite(planEndDDay) && planEndDDay <= COMPLETION_BOARD_START_D_DAY;

    const [remainingSeconds, setRemainingSeconds] = useState(() =>
        Math.ceil(Math.max(0, resolvedCountdownTarget - Date.now()) / 1000),
    );

    const currentTimeParts = getRemainingTimeParts(remainingSeconds);

    const previousTimePartsRef = useRef(currentTimeParts);
    const [previousTimeParts, setPreviousTimeParts] = useState(currentTimeParts);
    const [flipVersion, setFlipVersion] = useState(0);

    const currentTimeKey = currentTimeParts.join("");

    useEffect(() => {
        const previousParts = previousTimePartsRef.current;
        const previousTimeKey = previousParts.join("");

        if (previousTimeKey === currentTimeKey) return;

        setPreviousTimeParts(previousParts);
        previousTimePartsRef.current = currentTimeParts;
        setFlipVersion((currentVersion) => currentVersion + 1);
    }, [currentTimeKey]);

    useEffect(() => {
        if (!isCountdownActive) {
            const nextRemainingSeconds = Math.ceil(Math.max(0, resolvedCountdownTarget - Date.now()) / 1000);

            setRemainingSeconds(nextRemainingSeconds);
            return undefined;
        }

        let timeoutId = null;
        let isDisposed = false;

        const clearTimer = () => {
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
        };

        const updateCountdown = () => {
            clearTimer();

            if (isDisposed || (typeof document !== "undefined" && document.hidden)) {
                return;
            }

            const millisecondsLeft = Math.max(0, resolvedCountdownTarget - Date.now());

            const nextRemainingSeconds = Math.ceil(millisecondsLeft / 1000);

            setRemainingSeconds((currentSeconds) =>
                currentSeconds === nextRemainingSeconds ? currentSeconds : nextRemainingSeconds,
            );

            if (nextRemainingSeconds > 0) {
                const nextTickDelay = Math.max(50, millisecondsLeft - (nextRemainingSeconds - 1) * 1000);

                timeoutId = setTimeout(updateCountdown, nextTickDelay);
            }
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                clearTimer();
                return;
            }

            updateCountdown();
        };

        updateCountdown();

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            isDisposed = true;

            clearTimer();

            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [isCountdownActive, resolvedCountdownTarget]);

    const renderRemainingTime = () =>
        currentTimeParts.map((part, partIndex) => (
            <span key={`time-part-${partIndex}`} className="expln__cntdn-part">
                {Array.from(part).map((currentDigit, digitIndex) => {
                    const previousDigit = previousTimeParts[partIndex]?.[digitIndex] ?? currentDigit;

                    const isChanged = previousDigit !== currentDigit;

                    return (
                        <span
                            key={`digit-${partIndex}-${digitIndex}`}
                            className={`flip-number ${isChanged ? "is-flipping" : ""}`}
                        >
                            <span className="flip-number__half flip-number__half--top">
                                <span>{currentDigit}</span>
                            </span>

                            <span className="flip-number__half flip-number__half--bottom">
                                <span>{isChanged ? previousDigit : currentDigit}</span>
                            </span>

                            {isChanged && (
                                <React.Fragment key={`flip-${partIndex}-${digitIndex}-${flipVersion}`}>
                                    <span className="flip-number__leaf flip-number__leaf--top">
                                        <span>{previousDigit}</span>
                                    </span>

                                    <span className="flip-number__leaf flip-number__leaf--bottom">
                                        <span>{currentDigit}</span>
                                    </span>
                                </React.Fragment>
                            )}

                            <span className="flip-number__line" />
                        </span>
                    );
                })}

                <span className="expln__cntdn-unit">{REMAINING_TIME_UNITS[partIndex]}</span>
            </span>
        ));

    return (
        <header className="expln__hero">
            <div className="expln__status">
                <ExamPlanCompletionBoard
                    members={completionMembers}
                    mmbrSeq={mmbrSeq}
                    isVisible={showCompletionBoard}
                />

                <div className="expln__status-timer">
                    {showCountdown && (
                        <div className="expln__cntdn">
                            {!exam.hasPeriod ? (
                                isPlanEnded ? (
                                    "시험기간 미입력"
                                ) : (
                                    "시험기간을 입력해주세요."
                                )
                            ) : isExamEnded ? (
                                "시험 종료"
                            ) : (
                                <>
                                    시험까지 남은 시간
                                    <span className="expln__cntdn-time">{renderRemainingTime()}</span>
                                </>
                            )}
                        </div>
                    )}

                    <button type="button" className="expln__sem-settings" onClick={openExamSchedule} />
                </div>
            </div>

            <div className="expln__sem">
                <div className="expln__sem-summary">
                    <span className="expln__sem-grade">{exam.gradeSemester}</span>

                    <span className="expln__sem-dot" />

                    <span className="expln__sem-exam">{exam.name}</span>
                </div>

                {exam.period && (
                    <>
                        <span className="expln__sem-line" />

                        <div className="expln__sem-period">
                            <span className="expln__sem-cal" />

                            <span className="expln__sem-date">{exam.period}</span>
                        </div>
                    </>
                )}
            </div>
        </header>
    );
}

export default React.memo(ExamPlanHeader);
